<template>
  <div>
    <v-row>
      <!--table heading and search-->
      <v-col cols="12">
        <InvoicesTableHeadingAndSearch @filter="filterBy" />
      </v-col>

      <!--table filters-->
      <v-col cols="12">
        <InvoicesTableFilters
          @filter="filterBy"
          :loading="loading"
          :organizationProp="organization"
          :trainingProp="training"
          :startDateProp="datesRange.startDate"
          :endDateProp="datesRange.endDate"
        />
      </v-col>

      <!--table columns heading-->
      <v-col cols="12" v-if="$vuetify.breakpoint.mdAndUp">
        <v-card flat class="rounded-lg primary--text mt-3 px-md-2">
          <v-row align="center">
            <v-col cols="1" class="text-start ps-7">
              <div class="d-flex align-center">
                <v-checkbox
                  :input-value="isAllSelected"
                  @change="updateSelection(dataList, $event)"
                  :disabled="!dataList || !dataList.length"
                />

                {{ $t("all") }}
              </div>
            </v-col>

            <v-col cols class="text-start">
              {{ $t("invoice.organization") }}
            </v-col>

            <v-col cols class="text-start">
              {{ $t("invoice.invoiceNo") }}
            </v-col>

            <v-col cols="2" class="text-center">
              {{ $t("invoice.quantity") }}
            </v-col>

            <v-col cols="2" class="text-start">
              {{ $t("invoice.invoiceAmount") }}
            </v-col>

            <v-col cols="2" class="text-start">
              {{ $t("invoice.invoiceDate") }}
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <!--table body-->
      <v-col cols="12">
        <!--table rows-->
        <v-row v-if="!loading && dataList.length">
          <v-col cols="12" v-for="(data, index) in dataList" :key="index">
            <InvoicesTableRow
              :data="data"
              :isSelected="haveSelected(data)"
              @onChange="value => updateSelection([data], value)"
            />
          </v-col>
        </v-row>

        <!--table no items-->
        <v-row
          v-else-if="!loading && !dataList.length"
          class="textLight--text font-weight-medium my-16"
          align="center"
        >
          <v-col cols="12">
            {{ $t("noItems") }}
          </v-col>
        </v-row>

        <!--table loading-->
        <v-row v-else-if="loading && !dataList.length" class="my-16">
          <v-col cols="12">
            <v-progress-circular indeterminate color="primary" />
          </v-col>
        </v-row>
      </v-col>

      <!--table pagination-->
      <v-col cols="12" v-if="totalCount > pageInput.limit">
        <Pagination :length="totalPages" @onPageSelect="onPagination" />
      </v-col>

      <!--table bulk download-->
      <v-col cols="12" v-if="dataList && dataList.length">
        <div class="d-flex justify-end">
          <BulkDownloadButton
            :selections="selections"
            :downloading="downloading"
            @onClear="selections = []"
            @onClick="bulkDownload"
          />
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import InvoicesTableHeadingAndSearch from "./InvoicesTableHeadingAndSearch";
import InvoicesTableFilters from "./InvoicesTableFilters";
import InvoicesTableRow from "./InvoicesTableRow";
import BulkDownloadButton from "@/app/core-module/components/BulkDownloadButton";
import Pagination from "../../core-module/components/Pagination.vue";

import { bulkDownloadTrainingRegistrationInvoices } from "@/app/invoices-module/helpers/invoiceHelper";

export default {
  name: "InvoicesTable",
  components: {
    InvoicesTableHeadingAndSearch,
    InvoicesTableFilters,
    InvoicesTableRow,
    BulkDownloadButton,
    Pagination,
  },
  data() {
    return {
      loading: true,
      hasLoadingError: false,
      pageInput: this.defaultPageInput(),
      dataList: [],
      totalCount: null,
      search: null,
      organization: null,
      training: null,
      datesRange: {
        startDate: null,
        endDate: null,
      },
      selections: [],
      subscriberInfo: null,
      downloading: false,
      totalPages: 0,
    };
  },
  computed: {
    isAllSelected() {
      return (
        this.selections?.length &&
        this.dataList?.length &&
        this.dataList?.every(x => this.haveSelected(x))
      );
    },
  },
  apollo: {
    getTrainingRegistrationInvoices() {
      return {
        query: require("../api/query/getTrainingRegistrationInvoices.gql"),
        variables: { pageInput: this.pageInput, filterInput: {} },
        result({ data, loading }) {
          if (
            data?.getTrainingRegistrationInvoices?.trainingRegistrationInvoices
          ) {
            this.dataList =
              data.getTrainingRegistrationInvoices.trainingRegistrationInvoices;
            this.totalCount = data.getTrainingRegistrationInvoices.totalCount;
            let pagesVal = this.totalCount / this.pageInput.limit;
            this.totalPages = this.checkNumberIsFloat(pagesVal)
              ? Math.ceil(pagesVal)
              : pagesVal;
          } else {
            this.hasLoadingError = true;
          }

          this.loading = loading;
        },
      };
    },
    getSubscriberProfile() {
      return {
        query: require("../../user-module/api/query/getSubscriberProfile.gql"),
        result({ data }) {
          if (data?.getSubscriberProfile) {
            this.subscriberInfo = data.getSubscriberProfile;
          }
        },
      };
    },
  },
  methods: {
    checkNumberIsFloat(value) {
      return Number(value) === value && value % 1 !== 0;
    },
    haveSelected(obj) {
      return this.selections?.findIndex(x => x._id === obj._id) >= 0;
    },
    updateSelection(list, isSelected) {
      list.forEach(obj => {
        const existingIndex = this.selections.findIndex(x => x._id === obj._id);

        if (isSelected) {
          if (existingIndex < 0) {
            this.selections.push(obj);
          }
        } else {
          if (existingIndex >= 0) {
            this.selections.splice(existingIndex, 1);
          }
        }
      });
    },
    bulkDownload() {
      this.downloading = true;

      bulkDownloadTrainingRegistrationInvoices({
        trainingRegistrationInvoices: this.selections,
        subscriberInfo: this.subscriberInfo,
        // zip: true,
        // debug: true,
      }).then(response => {
        if (response) this.selections = [];
        this.downloading = false;
      });
    },
    onPagination(val) {
      if (val > 0) {
        this.pageInput.skip = this.pageInput.limit * (val - 1);
        this.refetch();
      }
    },
    filterBy(key, value) {
      if (key === "search") this.search = value;
      if (key === "organization") this.organization = value;
      if (key === "training") this.training = value;

      if (key === "dates") {
        this.datesRange.startDate = value.startDate;
        this.datesRange.endDate = value.endDate;
      }

      this.pageInput = this.defaultPageInput();

      this.refetch();
    },
    async refetch() {
      const filterConditions = {};

      if (this.search) filterConditions.search = this.search;

      if (this.organization) filterConditions.organization = this.organization;

      if (this.training) filterConditions.training = this.training;

      if (this.datesRange?.startDate) {
        filterConditions.dateFrom = this.datesRange.startDate;
      }

      if (this.datesRange?.endDate) {
        filterConditions.dateTo = this.datesRange.endDate;
      }

      await this.$apollo.queries.getTrainingRegistrationInvoices.refetch({
        pageInput: this.pageInput,
        filterInput: { ...filterConditions },
      });
    },
    resetFiltersAndRefresh() {
      this.pageInput = this.defaultPageInput();

      this.search = null;
      this.organization = null;
      this.training = null;
      this.datesRange = {
        startDate: null,
        endDate: null,
      };

      this.$apollo.queries.getTrainingRegistrationInvoices.setVariables({
        pageInput: this.pageInput,
        filterInput: {},
      });
    },
  },
};
</script>
